"use client";

import React, { useEffect, useMemo, useRef } from "react";
import { editor } from "monaco-editor";
import { DiffEditor, Editor } from "@monaco-editor/react";
import { initEditor } from "./monacoExtras";
import { usePrevious } from "@/utils/utils";
import { cn } from "@/lib/utils";

type MonacoType = typeof import("monaco-editor/esm/vs/editor/editor.api");

export interface Props {
  theme?: string;
  additionalOptions?: editor.IStandaloneEditorConstructionOptions;
  language?: string;

  value?: string;
  fileKey?: string;
  onChange?: (value: string) => void;

  // For the diff editor
  diff?: boolean;
  originalValue?: string;

  className?: string;
}

const SimpleMonacoEditor = ({
  theme,
  additionalOptions,
  value,
  onChange,
  fileKey,
  language,
  diff,
  originalValue,
  className,
}: Props) => {
  const monacoObjects = useRef<{
    editor: editor.IStandaloneCodeEditor;
    monaco: MonacoType;
  }>(null);
  const diffMonacoObjects = useRef<{
    editor: editor.IStandaloneDiffEditor;
    monaco: MonacoType;
  }>(null);

  // @ts-expect-error TS2345
  const [monacoModel, setMonacoModel] = React.useState<editor.ITextModel>(null);
  const [originalModel, setOriginalModel] =
    // @ts-expect-error TS2345
    React.useState<editor.ITextModel>(null);
  const [monacoLoaded, setMonacoLoaded] = React.useState(false);

  const prevFileKey = usePrevious(fileKey);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    try {
      if (!monacoObjects.current && !diffMonacoObjects.current) {
        return;
      }

      if (monacoModel && fileKey === prevFileKey && !monacoModel.isDisposed()) {
        const currentValue = monacoModel.getValue();
        if (currentValue !== value) {
          const range = monacoModel?.getFullModelRange();
          monacoModel.pushEditOperations(
            [],
            [
              {
                range: range,
                // @ts-expect-error TS2322
                text: value,
                forceMoveMarkers: true,
              },
            ],
            () => null,
          );
        }

        if (diff && originalModel) {
          const currentOriginalValue = originalModel.getValue();
          if (currentOriginalValue !== originalValue) {
            const range = originalModel?.getFullModelRange();
            originalModel.pushEditOperations(
              [],
              [
                {
                  range: range,
                  // @ts-expect-error TS2322
                  text: originalValue,
                  forceMoveMarkers: true,
                },
              ],
              () => null,
            );
          }
        }
      } else {
        // @ts-expect-error TS18047
        const newModel = monacoObjects.current.monaco.editor.createModel(
          value || "",
          language || "text",
        );

        newModel.onDidChangeContent((_) => {
          const newValue = newModel.getValue();
          if (onChangeRef.current) {
            onChangeRef.current(newValue);
          }
        });

        if (diff) {
          // @ts-expect-error TS18047
          const originalModel = monacoObjects.current.monaco.editor.createModel(
            originalValue || "",
            language || "text",
          );

          const modelToSet = {
            original: originalModel,
            modified: newModel,
          };

          diffMonacoObjects.current?.editor?.setModel(modelToSet);

          setOriginalModel(originalModel);
          setMonacoModel(newModel);
        } else {
          monacoObjects.current?.editor?.setModel(newModel);

          setMonacoModel(newModel);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [monacoLoaded, monacoObjects.current, fileKey, value, originalValue]);

  const handleEditorMount = (
    editor: editor.IStandaloneCodeEditor,
    monaco: MonacoType,
  ) => {
    // @ts-expect-error TS2540
    monacoObjects.current = {
      editor,
      monaco,
    };
    setMonacoLoaded(true);
  };

  const handleDiffEditorMount = (
    editor: editor.IStandaloneDiffEditor,
    monaco: MonacoType,
  ) => {
    // @ts-expect-error TS2540
    diffMonacoObjects.current = {
      editor,
      monaco,
    };
    setMonacoLoaded(true);
  };

  const { editor, diffEditor } = useMemo(() => {
    const options: editor.IStandaloneEditorConstructionOptions = {
      minimap: { enabled: false },
      overviewRulerLanes: 0,

      scrollbar: {
        vertical: "hidden",
        horizontal: "hidden",
        handleMouseWheel: true,
      },

      // Undocumented see https://github.com/Microsoft/vscode/issues/30795#issuecomment-410998882
      lineDecorationsWidth: 0,
      lineNumbersMinChars: 0,
      scrollBeyondLastLine: false,
      automaticLayout: true,
      wordWrap: "on",
      fontSize: 11,

      padding: {
        top: 4,
      },

      ...(additionalOptions || {}),
    };

    return {
      editor: (
        <Editor
          key="non-diff-editor"
          height={"100%"}
          width={"100%"}
          options={options}
          theme={theme || "vs-dark"}
          onMount={(editor, monaco) => {
            initEditor(monaco);

            if (theme) {
              monaco.editor.setTheme(theme);
            }

            handleEditorMount(editor, monaco);
          }}
          defaultLanguage="javascript"
        />
      ),
      diffEditor: (
        <DiffEditor
          key="diff-editor"
          options={{
            ...options,
            renderSideBySide: false,
          }}
          theme={theme || "vs-dark"}
          onMount={(editor, monaco) => {
            initEditor(monaco);

            if (theme) {
              monaco.editor.setTheme(theme);
            }

            handleDiffEditorMount(editor, monaco);
          }}
        />
      ),
    };
  }, []);

  return (
    <>
      <div
        data-testid="monaco-editor-container"
        className={cn(
          "h-[220px] pl-[15px] pt-2.5 bg-secondary-200 rounded-[5px] justify-center items-center gap-[25px] inline-flex container",
          diff && "hidden",
          className || "",
        )}
      >
        {editor}
      </div>
      <div
        data-testid="monaco-diff-editor-container"
        className={cn(
          "h-[220px] pl-[15px] pt-2.5 bg-secondary-200 rounded-[5px] justify-center items-center gap-[25px] inline-flex container",
          !diff && "hidden",
          className || "",
        )}
      >
        {diffEditor}
      </div>
    </>
  );
};

export default React.memo(SimpleMonacoEditor);
